import React, { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import TranslationCard from './components/TranslationCard';
import SearchBar from './components/SearchBar';
import HistoryList from './components/HistoryList';
import RecentList from './components/RecentList';
import CategoryView from './components/CategoryView';
import DataManagement from './components/DataManagement';
import { useTranslationStore } from './store/translationStore';
import { chineseTTS } from './utils/textToSpeech';

function App() {
  const [viewMode, setViewMode] = useState<'top' | 'recent' | 'categories'>('top');
  const { initializeDB, dbInitialized, setTTSAvailable } = useTranslationStore();

  // Initialize IndexedDB on mount
  useEffect(() => {
    console.log('Initializing local database...');
    initializeDB();
  }, [initializeDB]);

  // Initialize TTS availability check
  useEffect(() => {
    const initializeTTS = async () => {
      try {
        const isAvailable = await chineseTTS.isChineseAvailable();
        setTTSAvailable(isAvailable);
        console.log('TTS initialization:', isAvailable ? 'Available' : 'Not available');
      } catch (error) {
        console.error('TTS initialization failed:', error);
        setTTSAvailable(false);
      }
    };

    initializeTTS();
  }, [setTTSAvailable]);

  // Show loading state while initializing database
  if (!dbInitialized) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Toaster position="top-center" />

      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <header className="border-b border-gray-100 px-6 py-8">
          <div className="flex justify-between items-center mb-4">
            <div></div>
            <DataManagement />
          </div>
          <div className="flex items-center justify-center gap-3">
            <img
              src="/funyi_logo.png"
              alt="Funyi! Logo"
              className="h-12 w-12 object-contain"
            />
            <h1 className="text-3xl font-semibold text-gray-900">
              Funyi!
            </h1>
          </div>
          <p className="text-gray-500 text-center mt-2 text-sm">
            Keep track of what you have translated before and keep learning!
          </p>
        </header>

        {/* Main Content */}
        <div className="px-6">
          {/* Search Section */}
          <div className="py-8">
            <SearchBar />
          </div>

          {/* Translation Results */}
          <div className="pb-8">
            <TranslationCard />
          </div>

          {/* History Section - Always visible */}
          <div className="border-t border-gray-100 pt-8 pb-16">
            <div className="mb-6">
              <div className="flex items-center space-x-1 mb-6">
                <button
                  onClick={() => setViewMode('top')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'top'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Top Searches
                </button>
                <button
                  onClick={() => setViewMode('recent')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'recent'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Recent
                </button>
                <button
                  onClick={() => setViewMode('categories')}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${
                    viewMode === 'categories'
                      ? 'text-gray-900 border-b-2 border-gray-900'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Categories
                </button>
              </div>

              {viewMode === 'top' ? <HistoryList /> : viewMode === 'recent' ? <RecentList /> : <CategoryView />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;
